import {currentAttendanceReturn,safeAttendanceReturn} from './lib/attendance-return.js';
// pwa/app.js — NCSLSC TAF Hub PWA boot/mount entry (thin — ADR 20260527_PWAStructure).
// Wiring only: pick the entry view and mount it. Feature logic lives in lib/ + views/.

import { api } from './lib/api.js';
import { $session, hasAdminArea, can } from './lib/store.js';
import { showAdminSubnav, hideAdminSubnav } from './lib/admin-shell.js';
import { hasLearnAccess } from './lib/learn-access.js';
import { mountAuth } from './views/auth.js';
import { watchForUpdate, recoverFromStaleChunks } from './lib/sw-update.js';
import { watchSystem as watchThemeSystem } from './lib/theme.js';
import { watchInternalNavigation } from './lib/router.js';
import { mountHeader, setActiveRoute } from './lib/header.js';
import { installKeyboardShortcuts } from './components/keyboard-shortcuts.js';
import { surfaceTourForPath, getTourTips } from './components/tip-catalogue.js';
import { loadTipsState, isAutoplaying } from './lib/tips-state.js';
import { runFullTourStepIfActive, armFullTour } from './lib/full-tour.js';
import { legacyAdminTarget, parseAdminSection } from './lib/admin-sections.js';
import { trySectionSwitch, releaseSections } from './lib/admin-section-nav.js';

// Reconcile theme.dark with OS-level prefers-color-scheme changes for users
// whose preference is 'system'. The initial paint is handled by theme-init.js
// in <head> (CSP-safe; runs before stylesheet to avoid flash).
watchThemeSystem();

// Register the service worker (moved out of an inline <script> so the page CSP
// can keep script-src 'self' — see pwa/_headers). watchForUpdate listens for
// `controllerchange` and pops a "Refresh" banner when a new SW takes over an
// already-loaded tab — explicit user decision over silent reload.
// Recover from stale lazy chunks purged by a deploy (single guarded reload).
recoverFromStaleChunks();

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      if (!reg) return;
      // Proactively check for a new SW when the tab regains focus. The browser
      // only auto-checks /sw.js on navigation, so a long-lived tab that's never
      // reloaded wouldn't notice a deploy (nor pop the update banner) until the
      // next navigation. reg.update() forces the check on refocus.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') reg.update().catch(() => {});
      });
    }).catch(() => {});
  });
  watchForUpdate();
}

// ── Admin-area route gate (Feature 010) ─────────────────────────────────────
// Client-side cosmetic gate — the Worker's requireAdmin / requireCoordinator is
// the real security boundary. `perm` controls who (below full admin) may enter:
//   null         → any admin-area user (admin OR any coordinator) — e.g. Console
//   'admin'      → strict admin-only (e.g. Settings)
//   'x' | ['x',] → a coordinator holding ANY of these permission keys (admins always pass)
// A plain TAF (no admin area) → '/'; a coordinator lacking the surface's
// permission → '/admin' (Console, which every coordinator can reach).
function adminAllowed(me, perm) {
  if (!hasAdminArea(me)) return false;
  if (perm == null) return true;
  if (perm === 'admin') return !!me.is_admin;
  if (typeof perm === 'function') return !!me.is_admin || !!perm(me);
  const keys = Array.isArray(perm) ? perm : [perm];
  return !!me.is_admin || keys.some((k) => can(me, k));
}

// ── Onboarding consent gate (Feature 018) ───────────────────────────────────
// First sign-in (or a bumped policy version → re-consent, R10) must clear the consent
// wizard before any authed surface mounts. `me.needs_consent` is SERVER-computed (exact
// version match, red-team R13); app.js only reads the boolean — onboarding.js owns the
// detailed gate. Returns true if it redirected (caller must then stop).
function gateConsent(me) {
  if (me && me.needs_consent === true && (me.status === 'active' || me.status === 'invited')) {
    location.assign(`/onboarding${currentAttendanceReturn()?`?return_to=${encodeURIComponent(currentAttendanceReturn())}`:''}`);
    return true;
  }
  return false;
}

// ── Hub Tips: auto-play a surface's tour on its FIRST visit ──────────────────
// Chair decision (2026-06-22): each surface auto-plays its short tour once on first
// visit (then never again this session); the help button can always replay it. Fires
// after the view has mounted (called post-dispatch). Gated on the global autoplay pref
// + audience; startTour itself filters already-seen tips and missing DOM targets.
const _autoTourAttempted = new Set();
// "Reset tips & tours" (help popover) clears seen-state — re-arm auto-play so each
// surface's tour fires again on its next visit this session.
if (typeof window !== 'undefined') {
  window.addEventListener('hubtips:reset', () => _autoTourAttempted.clear());
}
async function maybeAutoTour(path) {
  // The cross-surface "full tour" takes over when active — it drives each leg + navigates.
  if (await runFullTourStepIfActive(path)) return;
  const me = $session.get();
  if (!me) return;
  const surface = surfaceTourForPath(path);
  if (!surface) return;
  // Admin-surface tours are for anyone who can reach that surface (admin OR coordinator).
  const isAdminArea = hasAdminArea(me);
  if (surface.audience === 'admin' && !isAdminArea) return;

  // An explicit replay was queued from the help hub (navigate-then-force). Honour it
  // regardless of seen/autoplay; otherwise fall through to first-visit auto-play.
  let replay = null;
  try { replay = sessionStorage.getItem('hubtips.replay'); } catch { /* private mode */ }
  const wantReplay = replay === surface.tour;

  if (wantReplay) {
    try { sessionStorage.removeItem('hubtips.replay'); } catch { /* ignore */ }
  } else {
    if (_autoTourAttempted.has(surface.tour)) return;   // auto once per session
    await loadTipsState();
    if (!isAutoplaying()) return;
    _autoTourAttempted.add(surface.tour);
  }

  const { startTour, isTourActive } = await import('./components/hub-tips.js');
  // Wait for the surface's PRIMARY (first) tip target to render before starting — surfaces
  // fetch data async, and starting too early skips the most important tip (the sign-up CTA
  // etc. — audit finding). Falls back after a timeout so a data-less surface still shows
  // whatever IS present.
  await _waitForTourTarget(surface.tour);
  if (!isTourActive()) startTour(surface.tour, { isAdmin: isAdminArea, force: wantReplay });
}

function _waitForTourTarget(tourId, timeoutMs = 3500) {
  const tips = getTourTips(tourId);
  if (!tips.length) return Promise.resolve();
  const primary = tips[0].targetSelector;
  const start = Date.now();
  return new Promise((resolve) => {
    const check = () => {
      const elapsed = Date.now() - start;
      if (document.querySelector(primary)) return resolve();            // primary ready → go
      if (elapsed > 1000 && tips.some((t) => document.querySelector(t.targetSelector))) return resolve(); // grace: any ready
      if (elapsed > timeoutMs) return resolve();                        // give up waiting; show what's there
      setTimeout(check, 150);
    };
    check();
  });
}

async function mountAdminArea(root, perm, loader) {
  try {
    // Use the cached session when present — admins/coordinators carry it from the
    // first load, so switching admin sub-tabs no longer pays an /api/auth/me
    // round-trip (the blank that read as a "refresh"). Mirrors mountTrainerRoute.
    // The server re-checks every request, so a cached role here is purely cosmetic;
    // we still revalidate in the background to pick up a grant change.
    const cached = $session.get();
    const me = cached || await api.get('/api/auth/me');
    if (!cached) $session.set(me);
    if (gateConsent(me)) return;              // F018: admins re-consent too (R10) before any admin surface
    if (!hasAdminArea(me)) { location.assign('/'); return; }
    if (!adminAllowed(me, perm)) { location.assign('/admin'); return; }
    // Persistent sub-nav: render the role-scoped bar into its own element (it
    // survives #app-main wipes). Authoritative `me` here covers the cold load
    // where dispatch's cached call no-opped.
    showAdminSubnav(me);
    await loader(root, me);
    if (cached) {
      api.get('/api/auth/me')
        .then((fresh) => { if (fresh) $session.set(fresh); })
        .catch(() => { /* keep the cached session on a transient failure */ });
    }
  } catch (e) {
    location.assign('/');
  }
}

// Path-based dispatch for the admin surfaces (Feature 005 / US3 + Feature 010).
function mountAdminRoute(root, { mode, sessionId, from = null, queue = null }) {
  return mountAdminArea(root, 'sessions.manage', async (r, me) => {
    const { mountAdminSessions } = await import('./views/admin-sessions.js');
    await mountAdminSessions(r, me, { mode, sessionId, from, queue });
  });
}

// /admin — the admin landing, now the "Console" (Feature 010). Any admin-area
// user (admin OR coordinator) lands here.
function mountAdminDashboardRoute(root) {
  return mountAdminArea(root, null, async (r, me) => {
    const { mountAdminDashboard } = await import('./views/admin-dashboard.js');
    await mountAdminDashboard(r, me);
  });
}

function mountAdminNoticeRoute(root) {
  return mountAdminArea(root, 'notices.manage', async (r, me) => {
    const { mountAdminNoticeCompose } = await import('./views/admin-notice-compose.js');
    await mountAdminNoticeCompose(r, me);
  });
}

// / — the trainer dashboard (spec 004). Mounts the dashboard DIRECTLY (no auth
// chrome) so SPA re-navigation re-renders cleanly and we never double-render the
// auth flow's own header into #app-main on top of the universal shell. Uses the
// cached $session when present (no /me round-trip); only an unauthenticated
// visitor falls through to the welcome/boot flow.
async function mountTrainerRoute(root) {
  const { mountTrainerDashboard } = await import('./views/trainer-dashboard.js');
  const cached = $session.get();
  if (cached) {
    if (gateConsent(cached)) return;          // F018: first sign-in → wizard before dashboard
    return mountTrainerDashboard(root, cached);
  }
  // Not cached: resolve the device session, then mount. The dashboard view
  // renders its own lit-html loading state — DON'T set root.innerHTML here, or
  // lit's render() leaves that manual markup behind it (the stale "Loading…").
  try {
    const me = await api.get('/api/auth/me');
    $session.set(me);
    if (gateConsent(me)) return;              // F018: first sign-in → wizard before dashboard
    return mountTrainerDashboard(root, me);
  } catch (e) {
    return mountAuth(root, { screen: 'boot' });
  }
}

// /roles — Roles & responsibilities TAF-facing org chart (auth-gated, not a nav tab).
async function mountRolesRoute(root) {
  const { mountRoles } = await import('./views/roles.js');
  const cached = $session.get();
  if (cached) {
    if (gateConsent(cached)) return;            // F018
    return mountRoles(root, cached);
  }
  try {
    const me = await api.get('/api/auth/me');
    $session.set(me);
    if (gateConsent(me)) return;
    return mountRoles(root, me);
  } catch (e) {
    return mountAuth(root, { screen: 'boot' });
  }
}

// /signup — the sessions board (re-hosted from / when the dashboard took the
// landing slot). Auth-gated; 401 → the welcome/boot flow.
async function mountSignupRoute(root) {
  const cached = $session.get();
  if (cached && gateConsent(cached)) return;   // F018: block un-consented nav-tab access
  try {
    const me = cached || await api.get('/api/auth/me');
    if (!cached) $session.set(me);
    if (gateConsent(me)) return;
    const { mountSessions } = await import('./views/sessions.js');
    await mountSessions(root, me);
  } catch (e) {
    return mountAuth(root, { screen: 'boot' });
  }
}

// /resources* — the Resources library (Feature F012). Auth-gated (TAF-only).
// Shares the trainer-route boot pattern: cached $session, else resolve, else boot.
async function mountResourcesArea(root, loader) {
  const cached = $session.get();
  if (cached) {
    if (gateConsent(cached)) return;            // F018: block un-consented nav to resources/courses/history
    return loader(root, cached);
  }
  try {
    const me = await api.get('/api/auth/me');
    $session.set(me);
    if (gateConsent(me)) return;
    return loader(root, me);
  } catch (e) {
    return mountAuth(root, { screen: 'boot' });
  }
}
function mountResourcesDashboardRoute(root) {
  return mountResourcesArea(root, async (r, me) => {
    const { mountResources } = await import('./views/resources.js');
    await mountResources(r, me);
  });
}
function mountResourcesAllRoute(root) {
  return mountResourcesArea(root, async (r, me) => {
    const { mountResourcesAll } = await import('./views/resource-all.js');
    await mountResourcesAll(r, me);
  });
}
function mountResourceCollectionRoute(root, code) {
  return mountResourcesArea(root, async (r, me) => {
    const { mountResourceCollection } = await import('./views/resource-collection.js');
    await mountResourceCollection(r, me, code);
  });
}
function mountResourceDocRoute(root, code, docCode) {
  return mountResourcesArea(root, async (r, me) => {
    const { mountResourceDoc } = await import('./views/resource-doc.js');
    await mountResourceDoc(r, me, code, docCode);
  });
}
// /resources/manage — the manage surface. Lives in the RESOURCES area (its own
// subnav), NOT the admin console (Chair directive). Still admin-only — writes are
// requireAdmin server-side; non-admins are bounced to /resources.
function mountResourcesManageRoute(root) {
  return mountResourcesArea(root, async (r, me) => {
    if (!me.is_admin) { location.assign('/resources'); return; }
    const { mountAdminResources } = await import('./views/admin-resources.js');
    await mountAdminResources(r, me);
  });
}
// /resources/manage/edit/:docId — the F012.1 in-Hub authoring editor (ADR 20260615).
// Gated on resources.edit (admins always); the Worker re-enforces on every write.
function mountResourcesEditRoute(root, docId) {
  return mountResourcesArea(root, async (r, me) => {
    if (!(me.is_admin || me.permissions?.['resources.edit'] === true)) { location.assign('/resources'); return; }
    const { mountAdminResourcesEdit } = await import('./views/admin-resources-edit.js');
    await mountAdminResourcesEdit(r, me, docId);
  });
}

// /courses/:id — TAF course-detail full page (F013). Auth-gated (TAF-only);
// session rows open the session-detail modal. Same boot pattern as Resources.
function mountCourseDetailRoute(root, courseId) {
  return mountResourcesArea(root, async (r, me) => {
    const { mountCourseDetail } = await import('./views/course-detail.js');
    await mountCourseDetail(r, me, courseId);
  });
}

// /history — TAF "My sessions & hours" (F016). Auth-gated (TAF-only); rows open
// the past-session modal. Same boot pattern as the course/resources pages.
function mountHistoryRoute(root) {
  return mountResourcesArea(root, async (r, me) => {
    const { mountHistory } = await import('./views/history.js');
    await mountHistory(r, me);
  });
}

// /admin/roster[/<section>] — batch 031 (decision 207): Allocations (F007) and TAF Logs (F3
// Confirm Centre) as ONE top tab. A coordinator needs allocations.manage OR taf-logs.view; the
// view shows only the sections their keys cover (the Worker re-checks every request).
function mountRosterRoute(root, section) {
  return mountAdminArea(root, ['allocations.manage', 'taf-logs.view'], async (r, me) => {
    const { mountAdminRoster } = await import('./views/admin-roster.js');
    await mountAdminRoster(r, me, { section });
  });
}

// /admin/course-overview — Feature 009 Course Overview = "Courses" (Feature 010),
// the single staffing/course home. /admin/kanban remains an alias.
function mountCourseOverviewRoute(root, section = null) {
  return mountAdminArea(root, ['courses.edit', 'sessions.manage'], async (r, me) => {
    const { mountAdminCourseOverview } = await import('./views/admin-course-overview.js');
    await mountAdminCourseOverview(r, me, { section });
  });
}

// /admin/people — F4 Layer 3 People lens = "Team" (Feature 010). Optional :id
// deep-links open the profile drawer on load.
function mountAdminPeopleRoute(root, personId = null, section = null) {
  // Team › Access requests is the members.manage coordinator's way in (it was /admin/access-requests).
  return mountAdminArea(root, ['people.view', 'members.manage'], async (r, me) => {
    const { mountAdminPeople } = await import('./views/admin-people.js');
    await mountAdminPeople(r, me, personId, { section });
  });
}

// /sessions/:id/confirm — F3 HT confirm view (HT or admin; Worker enforces).
async function mountHtConfirmRoute(root, sessionId) {
  try {
    const cached = $session.get();
    const me = cached || await api.get('/api/auth/me');
    if (!cached) $session.set(me);
    if (gateConsent(me)) return;                // F018
    const { mountHtConfirm } = await import('./views/ht-confirm.js');
    await mountHtConfirm(root, me, sessionId);
  } catch (e) {
    return mountAuth(root, { screen: 'boot' });
  }
}

// /confirm/:token — F3 self-confirm email landing. TOKEN-gated, NO auth round-trip
// (the token is the capability; the screen is inert until the user taps confirm).
async function mountConfirmLinkRoute(root, token) {
  const { mountConfirmLink } = await import('./views/confirm-link.js');
  await mountConfirmLink(root, token);
}

function mountAdminBulkRoute(root) {
  return mountAdminArea(root, 'sessions.manage', async (r, me) => {
    const { mountAdminSessionsBulk } = await import('./views/admin-sessions-bulk.js');
    await mountAdminSessionsBulk(r, me);
  });
}

function mountAdminCourseCreateRoute(root) {
  return mountAdminArea(root, 'courses.edit', async (r, me) => {
    const { mountAdminCourseCreate } = await import('./views/admin-course-create.js');
    await mountAdminCourseCreate(r, me);
  });
}

// /admin/learn — Feature 022 slice 3: Admin › Learn (content list, courses, item editor).
// Admins, plus coordinators holding any learn key (learn.pages, learn.practice,
// learn.programs:<uuid>). The Worker re-checks scope per item on every request.
function mountAdminLearnRoute(root, section) {
  return mountAdminArea(root, (me) => hasLearnAccess(me), async (r, me) => {
    const { mountAdminLearn } = await import('./views/admin-learn.js');
    await mountAdminLearn(r, me, { section });
  });
}
function mountAdminLearnEditRoute(root, itemId) {
  return mountAdminArea(root, (me) => hasLearnAccess(me), async (r, me) => {
    const { mountAdminLearnEdit } = await import('./views/admin-learn-edit.js');
    await mountAdminLearnEdit(r, me, itemId);
  });
}
// /admin/learn/awards — Feature 027: Hub › Learn › Awards (list, summary, edit, history). Admins and
// coordinators holding any learn key (learn.awards:<uuid> included); the Worker checks each award.
function mountAdminAwardsRoute(root, view, id = null) {
  return mountAdminArea(root, (me) => hasLearnAccess(me), async (r, me) => {
    const { mountAdminAwards } = await import('./views/admin-awards.js');
    const section = new URLSearchParams(location.search).get('section');
    await mountAdminAwards(r, me, { view, id, section });
  });
}
// /learn-preview/:token — public, token-gated draft preview (shared from the editor). No sign-in.
async function mountLearnPreviewRoute(root, token) {
  const { mountLearnPreviewPage } = await import('./views/learn-preview-page.js');
  await mountLearnPreviewPage(root, token);
}

// /admin/settings — Feature 010 Settings. Batch 032: Settings also holds Status (was /status) and
// Archive (was /admin/archive). Admin-only, except a coordinator holding archive.read still sees
// Archive › Past seasons (read-only), exactly as on the old Archive tab; the view scopes the
// sections and the Worker enforces every endpoint (requireAdmin / requireCoordinator).
function mountAdminSettingsRoute(root, section = null) {
  return mountAdminArea(root, 'archive.read', async (r, me) => {
    const { mountAdminSettings } = await import('./views/admin-settings.js');
    await mountAdminSettings(r, me, { section });
  });
}

// /onboarding — F018 public access-request wizard (A1→A4) + returning-device
// onboarding wizard (B1→B4, gate: me.privacy_acknowledged_at === null OR
// me.privacy_policy_version !== '2026-06-22'). The access-request form itself
// is fully public (no auth); the onboarding wizard is auth-gated inside the view.
async function mountOnboardingRoute(root, searchParams) {
  // Try to resolve session. If no session → public access-request form.
  // If session → show B-wizard if privacy gate applies, else redirect to /.
  try {
    const { mountAccessRequest } = await import('./components/access-request-form.js');
    const me = await api.get('/api/auth/me').catch(() => null);
    if (me) {
      $session.set(me);
      // If user is already onboarded, send them home.
      const { needsOnboarding } = await import('./views/onboarding.js');
      if (needsOnboarding(me)) {
        const { mountOnboarding } = await import('./views/onboarding.js');
        return mountOnboarding(root, me, {
          onComplete: ({ startTour } = {}) => {
            // B4 "Start tour" → arm the cross-surface full tour; it begins on the dashboard
            // after the reload (full-tour state lives in sessionStorage, survives reload).
            if (startTour) armFullTour();
            location.assign(currentAttendanceReturn()||'/');
          },
        });
      }
      location.assign(currentAttendanceReturn()||'/');
      return;
    }
    // No session → public access-request form (A1–A4).
    const prefillEmail = searchParams.get('email') || '';
    await mountAccessRequest(root, { prefillEmail });
  } catch {
    const { mountAccessRequest } = await import('./components/access-request-form.js');
    const prefillEmail = searchParams.get('email') || '';
    await mountAccessRequest(root, { prefillEmail });
  }
}

// /help/tutorials — Help hub (auth-gated; shows tour replay + guides).
async function mountHelpTutorialsRoute(root) {
  try {
    const cached = $session.get();
    const me = cached || await api.get('/api/auth/me');
    if (!cached) $session.set(me);
    if (gateConsent(me)) return;                // F018
    const { mountHelpTutorials } = await import('./views/help-tutorials.js');
    await mountHelpTutorials(root, me);
  } catch {
    return mountAuth(root, { screen: 'boot' });
  }
}

async function dispatch(main, path, params) {
  if (path === '' || path == null) path = '/';
  // Batch 031: old admin addresses (Allocations, TAF Logs, Access requests, the Courses aliases)
  // land on the matching section. Batch 032: so do /status (and /status#components etc.),
  // /admin/status (the alert emails) and /admin/archive/*, now inside Settings. The URL bar shows
  // the new address; the query and #fragment are kept (a /status#block fragment becomes the
  // section). Never break a bookmark: old emails stay in inboxes.
  const moved = legacyAdminTarget(path, location.hash);
  if (moved) {
    const q = params.toString();
    history.replaceState(history.state, '', `${moved.path}${q ? `?${q}` : ''}${moved.hash}`);
    path = moved.path;
  }
  // Another sub-section of the admin tab already on screen: switch in place (no remount, no
  // re-fetch, filters and selections kept). Reload / back / forward land here too.
  if (trySectionSwitch(path)) {
    setActiveRoute(path);
    showAdminSubnav($session.get());
    return;
  }
  releaseSections();
  // Give every view a FRESH inner container. The app mixes render primitives —
  // setHTML/innerHTML (auth, board) vs lit-html render (dashboard, admin) — on
  // the view target. lit caches a part (`_$litPart$`) on the container it
  // renders into; if a later innerHTML view wipes that container's children,
  // lit's cached part holds dead marker nodes, and the NEXT lit render into the
  // same node throws "insertBefore on null". Mounting each view into a brand-new
  // child node (discarded on the next nav) sidesteps the conflict entirely.
  main.replaceChildren();
  const view = /** @type {HTMLElement} */ (document.createElement('div'));
  view.className = 'view-root contents';
  main.appendChild(view);
  // Mark the active route — header updates the matching nav link's
  // aria-current attribute without re-painting the whole header.
  setActiveRoute(path);

  // Normalise an empty/odd pathname (bare-origin, trailing-slash quirks) to '/'.
  if (path === '' || path == null) path = '/';

  // Persistent admin sub-nav: reveal on every /admin* surface (instant from the
  // cached session — the gate re-renders with the authoritative one), hide
  // elsewhere. It lives outside #app-main so it never flashes on a sub-tab swap.
  if (path === '/admin' || path.startsWith('/admin/')) showAdminSubnav($session.get());
  else hideAdminSubnav();

  if (path.startsWith('/auth/confirm')) {
    return mountAuth(view, { screen: 'confirm', token: params.get('token') || '' });
  }

  // OAuth callback bounced back with a failure hint (?auth=not_setup|error|…).
  // Success carries no hint — the Worker redirects to "/" and boot resolves the
  // freshly-set cookie. Only failures route to the auth result screen.
  if (params.has('auth')) {
    return mountAuth(view, { screen: 'oauth', hint: params.get('auth') || '' });
  }

  // F018 — CENTRAL consent gate (critical): a user with a cached session who still
  // needs to consent cannot reach ANY authed surface via nav/URL until they accept
  // the privacy policy. This catches SPA nav-tab clicks (where $session is cached) in
  // one place + future-proofs new routes; cold loads (no cache) are still gated inside
  // each route's me-resolve. Exempt: the consent destination itself and token routes.
  if (path !== '/onboarding' && !path.startsWith('/confirm/') && gateConsent($session.get())) {
    return;
  }

  // F022 slice 3 — public draft preview link (token-gated; no session needed).
  const learnPreviewMatch = path.match(/^\/learn-preview\/([A-Za-z0-9_-]{20,100})$/);
  if (learnPreviewMatch) {
    return mountLearnPreviewRoute(view, learnPreviewMatch[1]);
  }
  // F022 slice 3 — Admin › Learn. Batch 031: /admin/learn[/<section>] is the Learn tab in the
  // shared shell (Awards included); editors and award pages below stay full-page drill-ins.
  const learnSection = parseAdminSection('learn', path);
  if (learnSection !== undefined) return mountAdminLearnRoute(view, learnSection);
  const awardMatch = path.match(/^\/admin\/learn\/awards\/([0-9a-fA-F-]{36})(?:\/(edit|history))?$/);
  if (awardMatch) return mountAdminAwardsRoute(view, awardMatch[2] === 'edit' ? 'edit' : awardMatch[2] === 'history' ? 'history' : 'summary', awardMatch[1].toLowerCase());
  const learnEditMatch = path.match(/^\/admin\/learn\/([0-9a-fA-F-]{36})$/);
  if (learnEditMatch) return mountAdminLearnEditRoute(view, learnEditMatch[1].toLowerCase());

  // Admin: create a new course (4-step wizard).
  if (path === '/admin/courses/new') {
    return mountAdminCourseCreateRoute(view);
  }
  // Admin: bulk-create.
  if (path === '/admin/sessions/new/bulk') {
    return mountAdminBulkRoute(view);
  }
  // Admin: compose notices (F4-Notices / spec 005).
  if (path === '/admin/notices') {
    return mountAdminNoticeRoute(view);
  }
  // Roles & responsibilities — TAF-facing org chart (auth-gated, not a nav tab).
  if (path === '/roles') {
    return mountRolesRoute(view);
  }
  // Sign up — the sessions board (re-hosted from /).
  if (path === '/signup') {
    return mountSignupRoute(view);
  }
  // Trainer dashboard — the `/` landing (spec 004).
  if (path === '/') {
    return mountTrainerRoute(view);
  }
  // Admin landing — the admin dashboard (spec 004, admin half).
  if (path === '/admin') {
    return mountAdminDashboardRoute(view);
  }
  // Batch 031: Roster = Allocations + TAF Logs (/admin/allocations, /admin/waitlists and
  // /admin/confirm redirect above). /admin/staffing and /admin/kanban redirect to Courses.
  const rosterSection = parseAdminSection('roster', path);
  if (rosterSection !== undefined) return mountRosterRoute(view, rosterSection);
  // Admin: Feature 009 Course Overview command centre = Courses.
  const coursesSection = parseAdminSection('courses', path);
  if (coursesSection !== undefined) return mountCourseOverviewRoute(view, coursesSection);
  // Admin: Feature 010 Settings (batch 032: with the Status and Archive groups).
  const settingsSection = parseAdminSection('settings', path);
  if (settingsSection !== undefined) return mountAdminSettingsRoute(view, settingsSection);
  // Resources library (Feature F012). Literal /all + /manage before the /:code
  // matcher (else they capture as collection codes); doc viewer before collection.
  if (path === '/resources') {
    return mountResourcesDashboardRoute(view);
  }
  if (path === '/resources/all') {
    return mountResourcesAllRoute(view);
  }
  if (path === '/resources/manage') {
    return mountResourcesManageRoute(view);
  }
  const resEditMatch = path.match(/^\/resources\/manage\/edit\/([^/]+)$/);
  if (resEditMatch) {
    return mountResourcesEditRoute(view, decodeURIComponent(resEditMatch[1]));
  }
  const resDocMatch = path.match(/^\/resources\/([^/]+)\/([^/]+)$/);
  if (resDocMatch) {
    return mountResourceDocRoute(view, decodeURIComponent(resDocMatch[1]), decodeURIComponent(resDocMatch[2]));
  }
  const resCollMatch = path.match(/^\/resources\/([^/]+)$/);
  if (resCollMatch) {
    return mountResourceCollectionRoute(view, decodeURIComponent(resCollMatch[1]));
  }
  // Admin: F4 People lens = Team. /admin/people/<section> first (directory, attention, skillmix,
  // roles, access); anything else after /admin/people/ is a person id that opens the drawer.
  const teamSection = parseAdminSection('team', path);
  if (teamSection !== undefined) return mountAdminPeopleRoute(view, null, teamSection);
  const peopleDeepLink = path.match(/^\/admin\/people\/([^/]+)$/);
  if (peopleDeepLink) {
    return mountAdminPeopleRoute(view, decodeURIComponent(peopleDeepLink[1]));
  }
  // F3 self-confirm email landing — token-gated, no auth.
  const confirmTokenMatch = path.match(/^\/confirm\/([^/]+)$/);
  if (confirmTokenMatch) {
    return mountConfirmLinkRoute(view, decodeURIComponent(confirmTokenMatch[1]));
  }
  // F3 HT / admin single-session confirm view.
  const htConfirmMatch = path.match(/^\/sessions\/([^/]+)\/confirm$/);
  if (htConfirmMatch) {
    return mountHtConfirmRoute(view, decodeURIComponent(htConfirmMatch[1]));
  }
  // F013: session-detail modal deep-link /sessions/:id — mount the board and open
  // the modal over it (mountSessions parses the path + cold-loads the session).
  // AFTER /sessions/:id/confirm above (specific-before-generic).
  const sessionDeepLink = path.match(/^\/sessions\/([^/]+)$/);
  if (sessionDeepLink) {
    return mountSignupRoute(view);
  }
  // F013: course-detail full page /courses/:id (TAF-scoped).
  const courseDeepLink = path.match(/^\/courses\/([^/]+)$/);
  if (courseDeepLink) {
    return mountCourseDetailRoute(view, decodeURIComponent(courseDeepLink[1]));
  }
  // F016: /history — My sessions & hours (TAF-scoped).
  if (path === '/attendance') {
    // Old mail/bookmarks share History; never mount a second attendance surface.
    location.replace(safeAttendanceReturn(location.pathname + location.search) || '/history');
    return;
  }
  if (path === '/history') {
    return mountHistoryRoute(view);
  }
  // F018: /onboarding — public access-request wizard (A-flow) + auth onboarding (B-flow).
  if (path === '/onboarding') {
    return mountOnboardingRoute(view, params);
  }
  // F019 /status is Settings › Status since batch 032 (redirected above).
  // F018: /help/tutorials — help hub page.
  if (path === '/help/tutorials') {
    return mountHelpTutorialsRoute(view);
  }
  // Admin: create.
  if (path === '/admin/sessions/new') {
    return mountAdminRoute(view, { mode: 'create' });
  }
  // Admin: edit /admin/sessions/{id}/edit — carries the dashboard deep-link
  // contract (?from={widget}&queue={comma-sep-ids}) into the form's queue strip.
  const editMatch = path.match(/^\/admin\/sessions\/([^/]+)\/edit$/);
  if (editMatch) {
    return mountAdminRoute(view, {
      mode: 'edit',
      sessionId: decodeURIComponent(editMatch[1]),
      from: params.get('from') || null,
      queue: params.get('queue') || null,
    });
  }

  // Default: resolve session (returning device) or show the welcome flow.
  return mountAuth(view, { screen: 'boot' });
}

function boot() {
  const headerEl = /** @type {HTMLElement|null} */ (document.getElementById('app-header'));
  const main = /** @type {HTMLElement|null} */ (document.getElementById('app-main'));
  if (!main || !headerEl) return;

  // Mount the universal header ONCE. Re-renders reactively on $session
  // changes (admin links flip on/off; hidden when no session); content
  // stays put across SPA navigation.
  mountHeader(headerEl);
  // Global keyboard shortcuts (Cmd/Ctrl+K, /, ?, g-then-letter, Esc fallback).
  installKeyboardShortcuts();

  // The magic-link URL lands on /auth/confirm?token=… — route straight to
  // the confirmation view (token is consumed on the POST there, never on
  // this GET).
  dispatch(
    main,
    window.location.pathname,
    new URLSearchParams(window.location.search),
  )
    .then(() => maybeAutoTour(window.location.pathname))
    .catch((e) => console.error('boot dispatch failed', e));

  // Same dispatch on in-app navigation. The router (lib/router.js)
  // hybridises document-click interception (works in every browser) with
  // the Navigation API (awaited so URL + content commit together on
  // Chromium / Firefox / new Safari).
  // Serialize navigations: each dispatch starts with main.replaceChildren(), so
  // two overlapping async dispatches would detach a node a sibling is mid-render
  // into. Chaining guarantees one-at-a-time. The promise is returned so the
  // Navigation API's intercept handler awaits the mount (URL + content commit
  // together).
  let navChain = Promise.resolve();
  watchInternalNavigation((path) => {
    const search = path.includes('?') ? path.slice(path.indexOf('?')) : '';
    const pathname = path.replace(/\?.*$/, '');
    navChain = navChain
      .then(() => dispatch(main, pathname, new URLSearchParams(search)))
      .then(() => maybeAutoTour(pathname))
      .catch((e) => console.error('dispatch failed', e));
    return navChain;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
